import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { RefreshCw, Banknote, Send, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI } from '../../lib/api';
import { fmt, paymentStatusBadge } from './_shared';

export function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [approvedStmts, setApprovedStmts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(null);
  const [failOpen, setFailOpen] = useState(null);
  const [emailOpen, setEmailOpen] = useState(null);  // { statement_id, payee_name }
  const [scheduleData, setScheduleData] = useState({ statement_id: '', payment_method: 'DIRECT_DEPOSIT', scheduled_date: '', notes: '' });
  const [completeData, setCompleteData] = useState({ reference_number: '', paid_date: '', notes: '' });
  const [failReason, setFailReason] = useState('');
  const [emailForm, setEmailForm] = useState({ to: '', note: '' });
  const [emailSending, setEmailSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, sr] = await Promise.all([
        compensationAPI.listPayments(),
        compensationAPI.listPayoutStatements({ status: 'Approved' }),
      ]);
      setPayments(pr.data.payments || []);
      setApprovedStmts(sr.data.statements || []);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const schedule = async () => {
    if (!scheduleData.statement_id) { toast.error('Pick a statement'); return; }
    try {
      await compensationAPI.schedulePayment(scheduleData);
      toast.success('Payment scheduled');
      setScheduleOpen(false);
      setScheduleData({ statement_id: '', payment_method: 'DIRECT_DEPOSIT', scheduled_date: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Schedule failed');
    }
  };

  const send = async (id) => {
    try { await compensationAPI.sendPayment(id); toast.success('Payment sent'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  const complete = async () => {
    if (!completeData.reference_number) { toast.error('Reference number required'); return; }
    try {
      const r = await compensationAPI.completePayment(completeOpen, completeData);
      toast.success(`Payment completed • ${r.data.cascaded_records} records → Paid (immutable)`);
      setCompleteOpen(null);
      setCompleteData({ reference_number: '', paid_date: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Complete failed');
    }
  };

  const fail = async () => {
    if (!failReason) { toast.error('Reason required'); return; }
    try {
      await compensationAPI.failPayment(failOpen, { reason: failReason });
      toast.success('Payment marked failed; statement re-opened for re-scheduling');
      setFailOpen(null);
      setFailReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fail update failed');
    }
  };

  const sendEmail = async () => {
    if (!emailOpen) return;
    setEmailSending(true);
    try {
      const r = await compensationAPI.emailPayoutStatement(emailOpen.statement_id, {
        to_email: emailForm.to.trim() || null,
        note: emailForm.note.trim() || null,
      });
      if (r.data.sent) toast.success(`Statement emailed to ${r.data.to_email}`);
      else toast.warning(`Email not sent: ${r.data.reason || 'unknown error'}`);
      setEmailOpen(null);
      setEmailForm({ to: '', note: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email request failed');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <Button onClick={() => setScheduleOpen(true)} data-testid="pay-schedule-btn"><Banknote className="h-4 w-4 mr-1" />Schedule Payment</Button>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="ml-auto">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payment</TableHead>
              <TableHead>Statement</TableHead>
              <TableHead>Payee</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>EFT</TableHead>
              <TableHead>Scheduled</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-zinc-500 py-8">No payments yet — schedule one from an Approved statement.</TableCell></TableRow>}
            {payments.map((p) => (
              <TableRow key={p.payment_id} data-testid={`pay-row-${p.payment_id}`}>
                <TableCell className="text-xs font-mono">{p.payment_id}</TableCell>
                <TableCell className="text-xs font-mono">{p.statement_id}</TableCell>
                <TableCell className="text-sm"><span className="font-medium">{p.payee_type}</span><br /><span className="text-xs text-zinc-500">{p.payee_name || p.payee_id}</span></TableCell>
                <TableCell className="text-right font-semibold">{fmt(p.amount)}</TableCell>
                <TableCell className="text-xs">{p.payment_method}</TableCell>
                <TableCell className="text-xs">{p.payment_method === 'DIRECT_DEPOSIT' ? (p.eft_approved ? <Badge className="bg-emerald-100 text-emerald-700">Approved</Badge> : <Badge className="bg-red-100 text-red-700">Missing</Badge>) : '—'}</TableCell>
                <TableCell className="text-sm">{p.scheduled_date}</TableCell>
                <TableCell className="text-xs">{p.reference_number || '—'}</TableCell>
                <TableCell>{paymentStatusBadge(p.status)}</TableCell>
                <TableCell className="flex flex-wrap gap-1">
                  {p.status === 'Scheduled' && <Button size="sm" onClick={() => send(p.payment_id)} data-testid={`pay-send-${p.payment_id}`}><Send className="h-3 w-3" />Send</Button>}
                  {(p.status === 'Scheduled' || p.status === 'Sent') && <Button size="sm" variant="outline" onClick={() => setCompleteOpen(p.payment_id)} data-testid={`pay-complete-${p.payment_id}`}>Complete</Button>}
                  <Button size="sm" variant="outline" onClick={() => { setEmailOpen({ statement_id: p.statement_id, payee_name: p.payee_name || p.payee_id }); setEmailForm({ to: '', note: '' }); }} data-testid={`pay-email-${p.payment_id}`}>
                    <Mail className="h-3 w-3" />Email
                  </Button>
                  {p.status !== 'Completed' && p.status !== 'Voided' && p.status !== 'Failed' && <Button size="sm" variant="ghost" onClick={() => setFailOpen(p.payment_id)} data-testid={`pay-fail-${p.payment_id}`}>Fail</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Schedule dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent data-testid="pay-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Payment</DialogTitle>
            <DialogDescription>Direct Deposit requires an Approved EFT on file for the payee.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Approved Statement</Label>
              <Select value={scheduleData.statement_id} onValueChange={(v) => setScheduleData((d) => ({ ...d, statement_id: v }))}>
                <SelectTrigger data-testid="pay-sched-stmt"><SelectValue placeholder="Pick a statement…" /></SelectTrigger>
                <SelectContent>
                  {approvedStmts.length === 0 && <div className="p-2 text-xs text-zinc-500">No Approved statements. Approve one first.</div>}
                  {approvedStmts.map((s) => (
                    <SelectItem key={s.statement_id} value={s.statement_id}>
                      {s.payee_type}: {s.payee_name || s.payee_id} • {fmt(s.final_total)} • {s.cycle_start?.slice(0, 10)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={scheduleData.payment_method} onValueChange={(v) => setScheduleData((d) => ({ ...d, payment_method: v }))}>
                <SelectTrigger data-testid="pay-sched-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DIRECT_DEPOSIT">Direct Deposit (EFT)</SelectItem>
                  <SelectItem value="CHECK">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scheduled Date</Label>
              <Input type="date" value={scheduleData.scheduled_date} onChange={(e) => setScheduleData((d) => ({ ...d, scheduled_date: e.target.value }))} data-testid="pay-sched-date" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={scheduleData.notes} onChange={(e) => setScheduleData((d) => ({ ...d, notes: e.target.value }))} data-testid="pay-sched-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button onClick={schedule} data-testid="pay-sched-confirm">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={!!completeOpen} onOpenChange={(o) => !o && setCompleteOpen(null)}>
        <DialogContent data-testid="pay-complete-dialog">
          <DialogHeader>
            <DialogTitle>Mark Payment Completed</DialogTitle>
            <DialogDescription>This cascades the statement and all its line-item compensation records to <strong>Paid (immutable)</strong>. Future corrections must go via the Adjustments ledger.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reference Number *</Label>
              <Input value={completeData.reference_number} onChange={(e) => setCompleteData((d) => ({ ...d, reference_number: e.target.value }))} placeholder="ACH trace # or check #" data-testid="pay-complete-ref" />
            </div>
            <div>
              <Label>Paid Date</Label>
              <Input type="date" value={completeData.paid_date} onChange={(e) => setCompleteData((d) => ({ ...d, paid_date: e.target.value }))} data-testid="pay-complete-date" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={completeData.notes} onChange={(e) => setCompleteData((d) => ({ ...d, notes: e.target.value }))} data-testid="pay-complete-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(null)}>Cancel</Button>
            <Button onClick={complete} data-testid="pay-complete-confirm">Mark Completed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fail dialog */}
      <Dialog open={!!failOpen} onOpenChange={(o) => !o && setFailOpen(null)}>
        <DialogContent data-testid="pay-fail-dialog">
          <DialogHeader>
            <DialogTitle>Mark Payment Failed</DialogTitle>
            <DialogDescription>Statement is rolled back to Approved so it can be re-scheduled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason *</Label>
              <Textarea value={failReason} onChange={(e) => setFailReason(e.target.value)} data-testid="pay-fail-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFailOpen(null)}>Cancel</Button>
            <Button onClick={fail} variant="destructive" data-testid="pay-fail-confirm">Mark Failed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog (inline, simple — full dialog with cc lives in StatementsTab) */}
      <Dialog open={!!emailOpen} onOpenChange={(o) => !o && setEmailOpen(null)}>
        <DialogContent data-testid="pay-email-dialog">
          <DialogHeader>
            <DialogTitle>Send Statement Email</DialogTitle>
            <DialogDescription>
              Manually email a PDF copy of the payee's statement. Email failures do NOT block the payment workflow.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-zinc-50 p-2 rounded">Payee: <strong>{emailOpen?.payee_name}</strong></div>
            <div>
              <Label>To (leave blank to use on-file email)</Label>
              <Input value={emailForm.to} onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))} placeholder="payee@example.com" data-testid="pay-email-to" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea value={emailForm.note} onChange={(e) => setEmailForm((f) => ({ ...f, note: e.target.value }))} rows={2} data-testid="pay-email-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(null)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending} data-testid="pay-email-confirm">
              <Mail className="h-4 w-4 mr-1" />{emailSending ? 'Sending…' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
